/**
 * Partner revenue-share state for the signed-in company.
 *
 * One RPC (`get_partner_referral_overview`) returns everything the referrer
 * screen needs so the page renders in a single deterministic pass — no
 * multi-query skeleton assembly. Bank details come back masked only; the full
 * account number never leaves the server.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface ReferredAccount {
  company_name: string;
  joined_at: string;
  status: string;
  plan_key: string | null;
}

export interface PayoutProfileMasked {
  account_holder_name: string;
  account_number_last4: string;
  routing_number_last4: string;
  account_type: 'checking' | 'savings';
  sec_code: 'CCD' | 'PPD';
  w9_submitted_at: string | null;
  payouts_enabled: boolean;
  updated_at: string;
}

export interface ReferralOverview {
  enrolled: boolean;
  lane: 'member' | 'partner' | null;
  status: 'active' | 'pending_review' | 'rejected' | 'suspended' | null;
  /** Suspended only because the company moved to Free — history kept, upgrade reactivates. */
  paused_for_plan?: boolean;
  code: string | null;
  active_referrals: number;
  current_tier: number;
  pending_cents: number;
  available_cents: number;
  disbursed_cents: number;
  lifetime_cents: number;
  min_payout_cents: number;
  accrual_hold_days: number;
  tax_form_threshold_cents: number;
  payout_profile: PayoutProfileMasked | null;
  referred_accounts: ReferredAccount[];
}

export interface PayoutDetailsInput {
  account_holder_name: string;
  routing_number: string;
  account_number: string;
  account_type: 'checking' | 'savings';
  sec_code: 'CCD' | 'PPD';
  tax_id_last4?: string | null;
  w9_submitted?: boolean;
}

/** Buyer invite link: credits the buyer, normal 30-day trial, no Partner RevShare needed. */
export function buyerInviteLinkFor(code: string): string {
  return `${window.location.origin}/j/${code}`;
}

/** The company's personal invite link (issued on first use). */
export function useBuyerInviteLink() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['buyer-invite-code', user?.id],
    enabled: !!user,
    staleTime: Infinity,
    queryFn: async (): Promise<string> => {
      const { data, error } = await supabase.rpc('issue_buyer_invite_code' as any);
      if (error) throw error;
      return buyerInviteLinkFor(data as unknown as string);
    },
  });
}

export function referralLinkFor(code: string): string {
  return `${window.location.origin}/r/${code}`;
}

export function useReferralProgram() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['referral-overview', user?.id],
    enabled: !!user,
    queryFn: async (): Promise<ReferralOverview> => {
      const { data, error } = await supabase.rpc('get_partner_referral_overview');
      if (error) throw error;
      return data as unknown as ReferralOverview;
    },
  });

  const enable = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('enable_referral_program');
      if (error) throw error;
      return data as unknown as { ok: boolean; code: string; lane: string; status: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-overview'] });
    },
  });

  const savePayoutDetails = useMutation({
    mutationFn: async (input: PayoutDetailsInput) => {
      const { data, error } = await supabase.functions.invoke('partner-payout-profile', {
        body: input,
      });
      if (error) {
        // Edge functions return a user-safe message; surface it verbatim.
        const message = (data as { error?: string } | null)?.error;
        throw new Error(message || 'We couldn\'t save those bank details. Please try again.');
      }
      return data as { ok: boolean; account_number_last4: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-overview'] });
    },
  });

  return {
    overview: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    enable,
    savePayoutDetails,
  };
}

const REFERRAL_STORAGE_KEY = 'cannafax_referral_code';
const REFERRAL_TTL_DAYS = 90;

/** Remember a referral code for 90 days. Overwrites: the last link clicked before signup wins; attribution locks at signup. */
export function storeReferralCode(
  code: string,
  source: ReferralSource = 'link',
  referrerName?: string | null,
  referrerLogoUrl?: string | null,
) {
  try {
    localStorage.setItem(
      REFERRAL_STORAGE_KEY,
      JSON.stringify({
        code: code.toUpperCase(),
        at: Date.now(),
        source,
        name: referrerName ?? null,
        logo: referrerLogoUrl ?? null,
      }),
    );
  } catch {
    // Private browsing / storage disabled — attribution is best-effort.
  }
}

/** Name of the company whose invitation is remembered (display only). */
export function readStoredReferrerName(): string | null {
  if (!readStoredReferralCode()) return null;
  try {
    const parsed = JSON.parse(localStorage.getItem(REFERRAL_STORAGE_KEY) || '{}') as { name?: string };
    return parsed.name || null;
  } catch {
    return null;
  }
}

/** Public logo of the inviting company (display only; https only). */
export function readStoredReferrerLogo(): string | null {
  if (!readStoredReferralCode()) return null;
  try {
    const parsed = JSON.parse(localStorage.getItem(REFERRAL_STORAGE_KEY) || '{}') as { logo?: string };
    return parsed.logo && /^https:\/\//.test(parsed.logo) ? parsed.logo : null;
  } catch {
    return null;
  }
}

export function readStoredReferralCode(): string | null {
  try {
    const raw = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { code?: string; at?: number };
    if (!parsed.code || !parsed.at) return null;
    const ageDays = (Date.now() - parsed.at) / 86_400_000;
    if (ageDays > REFERRAL_TTL_DAYS) {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      return null;
    }
    return parsed.code;
  } catch {
    return null;
  }
}

export function clearStoredReferralCode() {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // no-op
  }
}

export type ReferralSource = 'link' | 'share_page' | 'buyer_invite';

/** Where the stored code came from (defaults to a direct referral link). */
export function readStoredReferralSource(): ReferralSource {
  try {
    const raw = localStorage.getItem(REFERRAL_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as { source?: string }) : null;
    return parsed?.source === 'share_page' || parsed?.source === 'buyer_invite' ? parsed.source : 'link';
  } catch {
    return 'link';
  }
}
